import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { AddIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';
import { getTemplates, applyTemplateToMember, assignMembersToTemplate } from 'team/apis/teamTemplateApi';
import TeamChooseTemplateModal from 'team/components/editMember/TeamChooseTemplateModal';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamMemberTemplateButton = ({ member, teamId }) => {
    const { currentTeamId } = useTeam();
    const effectiveTeamId = teamId || currentTeamId;
    const [memberTemplate, setMemberTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const templateButtonRef = useRef(null);

    const memberUserId = member?.userId || member?.user?.userId || member?.user?._id || member?._id;
    const memberCardId = member?.defaultCard?._id;

    useEffect(() => {
        const fetchMemberTemplate = async () => {
            if (!memberUserId || !effectiveTeamId) return;

            setLoadingTemplate(true);
            try {
                const response = await getTemplates(effectiveTeamId);
                const templates = response?.templates || response?.data?.templates || response?.data || [];
                const appliedTemplateId = member?.defaultCard?.appliedTemplateId;

                let assignedTemplate = null;

                if (appliedTemplateId && Array.isArray(templates)) {
                    assignedTemplate = templates.find(template => {
                        const templateId = template?._id?.toString();
                        return templateId && templateId === appliedTemplateId.toString();
                    }) || null;
                }

                if (!assignedTemplate && Array.isArray(templates)) {
                    assignedTemplate = templates.find(template => {
                        if (!Array.isArray(template.assignedMembers)) return false;
                        return template.assignedMembers.some(assigned => {
                            if (!assigned) return false;
                            if (typeof assigned === 'string') {
                                return assigned.toString() === memberUserId.toString();
                            }
                            const assignedId = assigned.userId || assigned.user?._id || assigned._id;
                            return assignedId && assignedId.toString() === memberUserId.toString();
                        });
                    }) || null;
                }

                setMemberTemplate(assignedTemplate || null);
            } catch (error) {
                console.error('Error fetching member templates:', error);
                setMemberTemplate(null);
            } finally {
                setLoadingTemplate(false);
            }
        };

        fetchMemberTemplate();
    }, [memberUserId, effectiveTeamId, member?.defaultCard?.appliedTemplateId]);

    const handleTemplateDropdownOpen = (e) => {
        e.stopPropagation();
        if (!memberCardId) return;
        setIsTemplateModalOpen(true);
    };

    const handleTemplateDropdownClose = () => {
        setIsTemplateModalOpen(false);
    };

    const handleSelectTemplate = async (template) => {
        if (!template || !effectiveTeamId || !memberUserId) return;

        try {
            await applyTemplateToMember(effectiveTeamId, template._id, {
                memberId: memberUserId,
                cardId: memberCardId
            });
            setMemberTemplate(template);
            setIsTemplateModalOpen(false);
            setNotification({
                open: true,
                message: `Template "${template.templateName}" applied successfully`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Error applying template:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to apply template',
                severity: 'error'
            });
        }
    };

    const handleUnassignTemplate = async () => {
        if (!memberTemplate || !effectiveTeamId || !memberUserId) return;

        try {
            // Use existing assignMembersToTemplate API with unassignMemberIds
            await assignMembersToTemplate(effectiveTeamId, memberTemplate._id, {
                assignMemberIds: [],
                unassignMemberIds: [memberUserId]
            });
            setMemberTemplate(null);
            setNotification({
                open: true,
                message: 'Template removed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error removing template:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to remove template',
                severity: 'error'
            });
            throw error; // Re-throw to let modal know it failed
        }
    };

    const handleNotificationClose = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    const renderTemplateButtonIcon = () => {
        if (memberTemplate?.templateName) return null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ fontSize: '1rem', color: '#828282' }} />
                <Box
                    sx={{
                        backgroundColor: '#f7f7f7',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <AddIcon
                        sx={{
                            fontSize: '0.875rem',
                            color: '#828282'
                        }}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Button
                ref={templateButtonRef}
                variant="contained"
                onClick={handleTemplateDropdownOpen}
                data-member-action="template"
                sx={{
                    backgroundColor: memberTemplate?.templateName ? '#f2f2f2' : '#fff',
                    color: '#e0e0e0',
                    fontWeight: '500',
                    borderRadius: '999px',
                    border: memberTemplate?.templateName ? 'none' : '1px #e1e1e1 solid',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'auto',
                    padding: memberTemplate?.templateName ? '6.5px 12px' : '6.5px 10px 6.5px 12px',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: memberTemplate?.templateName ? '#f2f2f2' : '#fff',
                        color: '#e0e0e0',
                        boxShadow: 'none',
                    },
                    '& .MuiButton-startIcon': {
                        marginRight: memberTemplate?.templateName ? '8px' : 0,
                    }
                }}
                startIcon={renderTemplateButtonIcon()}
                disableRipple
            >
                {memberTemplate?.templateName && (
                    <Typography
                        sx={{
                            fontSize: '0.8rem',
                            color: '#333',
                            maxWidth: '110px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {memberTemplate.templateName}
                    </Typography>
                )}
            </Button>
            <TeamChooseTemplateModal
                open={isTemplateModalOpen}
                onClose={handleTemplateDropdownClose}
                onSelectTemplate={handleSelectTemplate}
                onUnassignTemplate={memberTemplate ? handleUnassignTemplate : undefined}
                cardId={memberCardId}
                currentAppliedTemplateId={memberTemplate?._id || member?.defaultCard?.appliedTemplateId}
            />
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />
        </>
    );
};

export default TeamMemberTemplateButton;
